import { escapeHtml } from '../format.js';
import { groupBySection, packColumns, type MenuBlock, type MenuColumn } from '../paginate.js';
import type { MenuBoardContent, MenuItemContent, MenuSection, MenuTheme } from '../types.js';
import { featuredItem } from './cafe-panels.js';
import {
  badge,
  bilingual,
  description,
  fitScale,
  hasTitle,
  image,
  lineCount,
  price,
  sectionSubtitle,
  sectionTitle,
  sellingPrice,
  variantLabel,
  type MenuStyleVariant,
  type MenuTemplate,
  type RenderContext,
} from './common.js';

/**
 * Fresh Drinks, layout A: a saturated colour panel on the left with the brand, a white ribbon
 * carrying the title in script over a huge capital word, and the featured drink in a round frame;
 * on the right a two-column grid of cards, each with a slanted colour bar for the name, the
 * sizes with dotted leaders, and the photo beside them.
 *
 * The title, size list, photo frame and colours are shared with the other two designs of the set
 * (fresh-drinks-grid, fresh-drinks-banner), so three screens side by side belong together.
 */

/* ------------------------------------------------------------- shared by the set */

export const FRESH_DRINKS_FAMILY = 'fresh-drinks';
export const FRESH_DRINKS_FAMILY_NAME = 'Fresh Drinks';

export const FRESH_DRINKS_THEME: MenuTheme = {
  backgroundColor: '#eef1ec',
  surfaceColor: '#ffffff',
  textColor: '#1d241e',
  primaryColor: '#16a03a',
  accentColor: '#ffd23f',
  fontId: 'cafe',
  backgroundMediaId: null,
};

export const FRESH_DRINKS_VARIANTS: MenuStyleVariant[] = [
  { id: 'fresh', name: 'Fresh green', theme: {} },
  { id: 'berry', name: 'Berry red', theme: { backgroundColor: '#f6eef1', textColor: '#2a1a20', primaryColor: '#c2185b', accentColor: '#ffd166' } },
  { id: 'ocean', name: 'Ocean blue', theme: { backgroundColor: '#edf2f7', textColor: '#16202b', primaryColor: '#0b6fc4', accentColor: '#ffd23f' } },
  { id: 'citrus', name: 'Orange citrus', theme: { backgroundColor: '#f8f1e9', textColor: '#2a1d12', primaryColor: '#ec7300', accentColor: '#ffe14d' } },
];

/** A glass outline for a drink with no photo. */
export const FD_CUP_SVG =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 64 64' fill='none' stroke='%23000' stroke-width='3' stroke-linejoin='round' stroke-linecap='round'%3E%3Cpath d='M16 18h32l-4 38H20z'/%3E%3Cpath d='M18 30h28'/%3E%3Cpath d='M36 18l6-12h8'/%3E%3C/svg%3E";

/** The photo of a drink, or a glass outline on a tint when it has none. */
export function fdPhoto(ctx: RenderContext, item: MenuItemContent, p: string): string {
  const img = image(ctx, item.imageMediaId, `${p}-img`);
  return img ? `<div class="${p}-ph">${img}</div>` : `<div class="${p}-ph ${p}-ph-none"><span></span></div>`;
}

/** "Cold Drinks" splits into a script "Cold" for the ribbon and a capital "DRINKS". */
function splitTitle(text: string | null | undefined): { script: string | null; caps: string } | null {
  const words = text?.trim().split(/\s+/).filter(Boolean) ?? [];
  if (!words.length) return null;
  if (words.length === 1) return { script: null, caps: words[0]! };
  return { script: words.slice(0, -1).join(' '), caps: words[words.length - 1]! };
}

/**
 * The set's title: a white ribbon with the script words over the last word in huge capitals.
 * On a bilingual board the Arabic title follows as a second line. Classes are `<p>-ttl`,
 * `-ribbon`, `-script`, `-caps`.
 */
export function fdTitle(ctx: RenderContext, p: string, maxCaps = 8): string {
  const { board } = ctx;
  const en = board.settings.title?.trim() || board.name;
  const ar = board.settings.titleAr?.trim() || null;
  const primary = ctx.showEn || !ar ? splitTitle(en) : splitTitle(ar);
  if (!primary) return '';
  const arabicMain = !(ctx.showEn || !ar);
  const dir = arabicMain ? ' dir="rtl" lang="ar"' : '';
  const capsScale = fitScale(primary.caps, maxCaps, 0.4);
  const scriptScale = fitScale(primary.script, 14, 0.5);
  const second =
    ctx.showEn && ctx.showAr && ar
      ? `<p class="${p}-caps mb-ar mb-2nd" dir="rtl" lang="ar">${escapeHtml(ar)}</p>`
      : '';
  return [
    `<div class="${p}-ttl">`,
    primary.script
      ? `<div class="${p}-ribbon"><span class="${p}-script${arabicMain ? ' mb-ar' : ''}"${dir} style="font-size:${scriptScale.toFixed(3)}em">${escapeHtml(primary.script)}</span></div>`
      : '',
    `<p class="${p}-caps${arabicMain ? ' mb-ar' : ''}"${dir}><span style="font-size:${capsScale.toFixed(3)}em">${escapeHtml(primary.caps)}</span></p>`,
    second,
    '</div>',
  ].join('');
}

/** The brand's logo, or its name, for the top of a panel. */
export function fdBrand(ctx: RenderContext, p: string): string {
  const logo = image(ctx, ctx.board.brand.logoMediaId, `${p}-logo`);
  if (logo) return `<div class="${p}-brand">${logo}</div>`;
  const name = bilingual(ctx, ctx.board.brand.name, ctx.board.brand.nameAr, `${p}-brandname`);
  return name ? `<div class="${p}-brand">${name}</div>` : '';
}

/** The name in the board's first language and, on a bilingual board, the Arabic one. */
export function fdNames(ctx: RenderContext, item: MenuItemContent, p: string): string {
  const ar = item.titleAr?.trim();
  if (!ctx.showEn && ar) return `<span class="${p}-name mb-ar" dir="rtl" lang="ar">${escapeHtml(ar)}</span>`;
  const second = ctx.showEn && ctx.showAr && ar ? `<span class="${p}-name2 mb-ar" dir="rtl" lang="ar">${escapeHtml(ar)}</span>` : '';
  return `<span class="${p}-name">${escapeHtml(item.title)}</span>${second}`;
}

/** Sizes as rows with dotted leaders, or the one price (struck through when discounted). */
export function fdSizes(ctx: RenderContext, item: MenuItemContent, p: string): string {
  if (!item.variants.length) return `<div class="${p}-one">${sellingPrice(ctx, item, `${p}-oneprice`)}</div>`;
  return `<div class="${p}-sizes">${item.variants
    .map(
      (v) =>
        `<div class="${p}-sz"><span class="${p}-szl">${variantLabel(ctx, v.label, v.labelAr)}</span><span class="${p}-dots"></span><span class="${p}-szp" dir="ltr">${price(ctx, v.price)}</span></div>`,
    )
    .join('')}</div>`;
}

/** Whether a bar holding both names wraps to a second line. */
export function namesWrap(board: MenuBoardContent, item: MenuItemContent, chars: number): boolean {
  const lang = board.settings.language;
  const en = lang === 'ar' && item.titleAr ? 0 : item.title.length;
  const ar = lang === 'both' && item.titleAr ? item.titleAr.length * 0.8 + 2 : lang === 'ar' && item.titleAr ? item.titleAr.length * 0.8 : 0;
  return en + ar > chars;
}

/** Description lines shown under an item, clamped to `max` per language. */
export function descLines(board: MenuBoardContent, item: MenuItemContent, perLine: number, max: number): number {
  if (!board.settings.showDescriptions) return 0;
  const lang = board.settings.language;
  return (
    (lang !== 'ar' || !item.descriptionAr ? lineCount(item.description, perLine, max) : 0) +
    (lang !== 'en' ? lineCount(item.descriptionAr, perLine, max) : 0)
  );
}

/** Ribbon, capitals, slanted bars, size rows and photo frames under `S` with prefix `p`. */
export function fdSharedCss(S: string, p: string): string {
  return `
${S} .${p}-ttl{display:flex;flex-direction:column;align-items:center;text-align:center;min-width:0}
${S} .${p}-ribbon{position:relative;background:var(--mb-surface);padding:.05em 1.1em .15em;margin:0 1.6em;
  box-shadow:0 .25em .6em rgba(0,0,0,.18);font-size:3.4em;line-height:1.15;white-space:nowrap}
${S} .${p}-ribbon:before,${S} .${p}-ribbon:after{content:'';position:absolute;top:.28em;bottom:-.28em;width:1.1em;
  background:var(--mb-surface);opacity:.78;z-index:-1}
${S} .${p}-ribbon:before{right:100%;margin-right:-.3em;clip-path:polygon(0 0,100% 0,100% 100%,0 100%,.4em 50%)}
${S} .${p}-ribbon:after{left:100%;margin-left:-.3em;clip-path:polygon(0 0,100% 0,calc(100% - .4em) 50%,100% 100%,0 100%)}
${S} .${p}-script{font-family:var(--mb-font-script);color:var(--mb-primary);display:inline-block;padding:0 .12em;vertical-align:bottom}
${S} .${p}-script.mb-ar{line-height:1.5}
${S} .${p}-caps{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);color:#fff;text-transform:uppercase;
  font-size:8em;line-height:1;letter-spacing:.01em;white-space:nowrap;text-shadow:0 .04em .12em rgba(0,0,0,.25)}
${S} .${p}-caps.mb-ar{line-height:1.35}
${S} .${p}-caps.mb-2nd{font-size:3em;white-space:normal;margin-top:-.1em}
${S} .${p}-brand{display:flex;flex-direction:column;align-items:center;text-align:center}
${S} .${p}-logo{height:5em;max-width:14em;object-fit:contain}
${S} .${p}-brandname{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);color:#fff;font-size:1.6em;
  letter-spacing:.2em;text-transform:uppercase;line-height:1.15}
${S} .${p}-brandname.mb-2nd{font-size:1.3em;letter-spacing:0}
${S} .${p}-sizes{display:flex;flex-direction:column}
${S} .${p}-sz{display:flex;align-items:baseline;gap:.35em;font-size:1.2em;line-height:1.45;white-space:nowrap}
${S} .${p}-szl{font-weight:500;overflow:hidden;text-overflow:ellipsis;min-width:0;flex:0 1 auto}
${S} .${p}-dots{flex:1 0 1em;border-bottom:.16em dotted color-mix(in srgb,var(--mb-text) 55%,transparent);transform:translateY(-.25em)}
${S} .${p}-szp{font-weight:800;color:var(--mb-text)}
${S} .${p}-one{display:flex;align-items:baseline;gap:.6em;flex-wrap:wrap}
${S} .${p}-oneprice{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:2.2em;color:var(--mb-primary);line-height:1.1}
${S} .${p}-oneprice-old{font-size:1.3em;opacity:.55;font-weight:700}
${S} .${p}-ph{overflow:hidden;background:color-mix(in srgb,var(--mb-primary) 12%,var(--mb-surface))}
${S} .${p}-img{width:100%;height:100%;object-fit:cover;display:block}
${S} .${p}-ph-none{display:flex;align-items:center;justify-content:center}
${S} .${p}-ph-none span{width:45%;aspect-ratio:1;background:var(--mb-primary);opacity:.45;
  -webkit-mask:url("${FD_CUP_SVG}") center/contain no-repeat;mask:url("${FD_CUP_SVG}") center/contain no-repeat}
${S} .${p}-badges{display:inline-flex;gap:.3em;flex-shrink:0}
${S} .${p}-badge{font-family:var(--mb-font-body);font-size:.6em;font-weight:800;padding:.2em .55em;border-radius:99em;
  background:var(--mb-accent);color:var(--mb-text);white-space:nowrap;letter-spacing:0}
${S} .${p}-desc{font-size:.9em;opacity:.7;line-height:1.3;display:-webkit-box;-webkit-line-clamp:1;-webkit-box-orient:vertical;overflow:hidden}
`;
}

/* ------------------------------------------------------------------ this design */

const S = '.mb-t-fresh-drinks-cards';

function card(ctx: RenderContext, item: MenuItemContent): string {
  const badges = badge(ctx, item, 'fdc-badge');
  return [
    `<div class="fdc-card">`,
    `<div class="fdc-bar">${fdNames(ctx, item, 'fdc')}${badges ? `<span class="fdc-badges">${badges}</span>` : ''}</div>`,
    `<div class="fdc-info">${fdSizes(ctx, item, 'fdc')}${description(ctx, item, 'fdc-desc')}</div>`,
    fdPhoto(ctx, item, 'fdc'),
    '</div>',
  ].join('');
}

function block(ctx: RenderContext, b: MenuBlock): string {
  const head = hasTitle(b.section)
    ? `<div class="fdc-sec">${sectionTitle(ctx, b.section, 'fdc-sec-title')}${b.continued ? '' : sectionSubtitle(ctx, b.section, 'fdc-sec-sub')}<span class="fdc-sec-line"></span></div>`
    : '';
  return `<div class="fdc-block">${head}${b.items.map((i) => card(ctx, i)).join('')}</div>`;
}

function featuredPanel(ctx: RenderContext, item: MenuItemContent): string {
  const sizes = item.variants.length
    ? `<div class="fdc-ft-sizes">${item.variants
        .map((v) => `<span class="fdc-ft-sz"><small>${variantLabel(ctx, v.label, v.labelAr)}</small> <b dir="ltr">${price(ctx, v.price)}</b></span>`)
        .join('')}</div>`
    : '';
  const burst = item.variants.length ? '' : `<div class="fdc-burst">${sellingPrice(ctx, item, 'fdc-bp')}</div>`;
  return [
    '<div class="fdc-ft">',
    `<div class="fdc-ft-frame">${fdPhoto(ctx, item, 'fdc-ft')}${burst}</div>`,
    `<div class="fdc-ft-text"><div style="font-size:${fitScale(item.title, 18, 0.6).toFixed(3)}em">${bilingual(ctx, item.title, item.titleAr, 'fdc-ft-name')}</div>${sizes}</div>`,
    '</div>',
  ].join('');
}

/** Card heights in em at the reference size, so each column only takes what it can show. */
function cardColumns(board: MenuBoardContent, portrait: boolean): MenuColumn[] {
  const both = board.settings.language === 'both';
  const cardHeight = (item: MenuItemContent) => {
    const bar = 2.9 + (namesWrap(board, item, portrait ? 22 : 28) ? 2 : 0);
    const rows = item.variants.length ? item.variants.length * (portrait ? 1.92 : 1.74) : 2.5;
    const desc = descLines(board, item, portrait ? 30 : 40, 1) * 1.17;
    return Math.max(10.5, bar + 1.3 + rows + desc);
  };
  const column: MenuColumn = {
    capacity: portrait ? 71 : 61.5,
    blockCost: (section: MenuSection, items) =>
      (hasTitle(section) ? (both && section.titleAr ? 3.6 : 3.2) : 0) +
      items.reduce((sum, item) => sum + cardHeight(item) + 1.2, 0),
  };
  return [column, column];
}

export const freshDrinksCards: MenuTemplate = {
  id: 'fresh-drinks-cards',
  name: 'Fresh drinks · Hero & cards',
  description:
    'A bright colour panel with a ribbon title and your featured drink, beside cards with slanted name bars, every size with dotted leaders and a photo.',
  defaultTheme: FRESH_DRINKS_THEME,
  variants: FRESH_DRINKS_VARIANTS,
  cuisines: ['drinks', 'cafe'],
  family: { id: FRESH_DRINKS_FAMILY, name: FRESH_DRINKS_FAMILY_NAME, layout: 'Hero & cards' },
  uses: { featured: true, sectionImages: false, brand: true, decorations: [] },

  paginate(board, orientation) {
    const featured = featuredItem(board);
    return packColumns(groupBySection(board, featured?.id), cardColumns(board, orientation === 'portrait'), { balance: true });
  },

  render(ctx) {
    const { board } = ctx;
    const item = featuredItem(board);
    const tagline = bilingual(ctx, board.brand.tagline, board.brand.taglineAr, 'fdc-tag');
    const panel = [
      '<div class="fdc-panel">',
      fdBrand(ctx, 'fdc'),
      `<div class="fdc-head">${fdTitle(ctx, 'fdc')}</div>`,
      item ? featuredPanel(ctx, item) : '<div class="fdc-ft fdc-ft-empty"></div>',
      tagline ? `<div class="fdc-tags">${tagline}</div>` : '',
      '</div>',
    ].join('');
    const empty = ctx.page.columns.every((c) => c.length === 0);
    const main = empty
      ? item
        ? ''
        : '<div class="mb-empty">No drinks on this board yet</div>'
      : `<div class="fdc-cols">${ctx.page.columns
          .map((c) => `<div class="fdc-col">${c.map((b) => block(ctx, b)).join('')}</div>`)
          .join('')}</div>`;
    return `${panel}<div class="fdc-main">${main}</div>`;
  },

  css: `
${S}{display:grid;grid-template-columns:31% 1fr;grid-template-rows:100%;padding:0}
${S}.mb-portrait{grid-template-columns:100%;grid-template-rows:38em minmax(0,1fr)}
${S}>div{min-width:0;min-height:0}
${fdSharedCss(S, 'fdc')}
${S} .fdc-panel{position:relative;overflow:hidden;background:var(--mb-primary);color:#fff;padding:2.4em 2.6em 2.2em;
  display:flex;flex-direction:column;align-items:center;gap:1.4em;
  background-image:radial-gradient(circle at 30% 20%,rgba(255,255,255,.22),transparent 55%),
    radial-gradient(circle at 80% 105%,rgba(0,0,0,.25),transparent 55%),
    repeating-conic-gradient(from 0deg at 50% 62%,rgba(255,255,255,.05) 0 6deg,transparent 6deg 14deg)}
${S}.mb-portrait .fdc-panel{display:grid;grid-template-columns:1fr 1fr;grid-template-rows:auto 1fr auto;
  column-gap:3em;row-gap:1em;padding:2.4em 3.4em;align-items:center}
${S}.mb-portrait .fdc-brand{grid-column:1;grid-row:1}
${S}.mb-portrait .fdc-head{grid-column:1;grid-row:2}
${S}.mb-portrait .fdc-tags{grid-column:1;grid-row:3}
${S}.mb-portrait .fdc-ft{grid-column:2;grid-row:1 / span 3;height:100%}
${S} .fdc-head{width:100%;display:flex;justify-content:center}
${S}.mb-portrait .fdc-caps:not(.mb-2nd){font-size:7em}
${S} .fdc-ft{flex:1 1 0;min-height:0;width:100%;display:flex;flex-direction:column;align-items:center;gap:1em}
${S} .fdc-ft-frame{position:relative;flex:1 1 0;min-height:6em;max-height:24em;aspect-ratio:1;max-width:100%}
${S} .fdc-ft-ph{width:100%;height:100%;border-radius:50%;border:.55em solid var(--mb-surface);
  box-shadow:0 1em 2.2em rgba(0,0,0,.3)}
${S} .fdc-burst{position:absolute;right:-4%;bottom:4%;width:8.5em;height:8.5em;border-radius:50%;background:var(--mb-accent);
  color:var(--mb-text);display:flex;flex-direction:column;align-items:center;justify-content:center;transform:rotate(-8deg);
  box-shadow:0 .5em 1.2em rgba(0,0,0,.3);outline:.25em dashed rgba(255,255,255,.7);outline-offset:-.6em}
${S} .fdc-bp{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:2em;line-height:1}
${S} .fdc-bp-old{font-size:1.1em;opacity:.65;font-weight:700}
${S} .fdc-ft-text{text-align:center;flex-shrink:0}
${S} .fdc-ft-name{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:2.4em;text-transform:uppercase;line-height:1.1}
${S} .fdc-ft-name.mb-2nd{font-size:1.7em;text-transform:none}
${S} .fdc-ft-sizes{display:flex;flex-wrap:wrap;justify-content:center;gap:.4em .6em;margin-top:.5em}
${S} .fdc-ft-sz{background:rgba(0,0,0,.18);border-radius:99em;padding:.25em .8em;font-size:1.05em;white-space:nowrap}
${S} .fdc-ft-sz small{font-size:.85em;opacity:.85}
${S} .fdc-ft-sz b{color:var(--mb-accent);font-weight:800}
${S} .fdc-tags{flex-shrink:0;text-align:center;max-width:28em}
${S} .fdc-tag{font-size:1.05em;line-height:1.4;opacity:.92;display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden}
${S} .fdc-tag.mb-2nd{margin-top:.3em}
${S} .fdc-main{background:var(--mb-bg);padding:2.2em 2.6em;display:flex;flex-direction:column;min-height:0;overflow:hidden;
  background-image:linear-gradient(135deg,rgba(255,255,255,.55),transparent 60%)}
${S} .fdc-cols{flex:1;min-height:0;display:grid;grid-template-columns:1fr 1fr;gap:2em}
${S} .fdc-col{min-width:0;display:flex;flex-direction:column;gap:1.2em}
${S} .fdc-block{display:contents}
${S} .fdc-sec{display:flex;align-items:baseline;gap:.7em;height:2em;margin-bottom:-.2em}
${S} .fdc-sec-title{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:1.7em;text-transform:uppercase;
  color:var(--mb-primary);letter-spacing:.04em;line-height:1;white-space:nowrap}
${S} .fdc-sec-title.mb-2nd{font-size:1.4em}
${S} .fdc-sec-sub{font-family:var(--mb-font-script);color:var(--mb-text);opacity:.7;font-size:1.3em;white-space:nowrap}
${S} .fdc-sec-sub.mb-2nd{display:none}
${S} .fdc-sec-line{flex:1;height:.18em;background:var(--mb-primary);opacity:.35;align-self:center}
${S} .fdc-sec{flex:none}
${S} .fdc-card{flex:1 1 auto;max-height:15em;display:grid;grid-template-columns:minmax(0,1fr) 9.5em;grid-template-rows:auto 1fr;min-height:10.5em;
  background:var(--mb-surface);border-radius:.5em;overflow:hidden;box-shadow:0 .35em 1em rgba(0,0,0,.1)}
${S}.mb-portrait .fdc-card{grid-template-columns:minmax(0,1fr) 11em;max-height:16.5em}
${S}.mb-portrait .fdc-sz{font-size:1.32em}
${S}.mb-portrait .fdc-name{font-size:1.85em}
${S} .fdc-bar{grid-column:1;grid-row:1;display:flex;align-items:center;flex-wrap:wrap;gap:0 .6em;background:var(--mb-primary);color:#fff;
  padding:.3em 2em .3em 1em;min-height:2.9em;margin-inline-end:-1.2em;position:relative;z-index:1;
  clip-path:polygon(0 0,100% 0,calc(100% - 1.3em) 100%,0 100%)}
${S}[dir=rtl] .fdc-bar{clip-path:polygon(0 0,100% 0,100% 100%,1.3em 100%)}
${S} .fdc-name{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:1.65em;text-transform:uppercase;
  line-height:1.1;letter-spacing:.02em;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:100%}
${S} .fdc-name2{font-family:var(--mb-font-heading);font-size:1.3em;margin-inline-start:auto;white-space:nowrap;overflow:hidden;
  text-overflow:ellipsis;max-width:100%;opacity:.95}
${S} .fdc-info{grid-column:1;grid-row:2;padding:.6em 1em .7em 1.1em;display:flex;flex-direction:column;justify-content:center;gap:.3em;min-width:0}
${S} .fdc-ph{grid-column:2;grid-row:1 / span 2;margin:.55em;border-radius:.45em;min-height:0}
${S} .fdc-ft-ph{overflow:hidden;background:color-mix(in srgb,var(--mb-primary) 12%,var(--mb-surface))}
${S} .fdc-ft-img{width:100%;height:100%;object-fit:cover;display:block}
${S} .fdc-ft-ph-none{display:flex;align-items:center;justify-content:center}
${S} .fdc-ft-ph-none span{width:45%;aspect-ratio:1;background:var(--mb-primary);opacity:.45;
  -webkit-mask:url("${FD_CUP_SVG}") center/contain no-repeat;mask:url("${FD_CUP_SVG}") center/contain no-repeat}
`,
};
